<template>
  <ProfileTemplate title="Profile">
    <ProfileHeaderCardWidget/>

    <form>
      <div class="container container-fixed-992 mb-5">
        <div class="row">
          <div class="col-12">
            <FormProfileGroupSection
              :form-element="firstNameInput"
              v-model:input-value="firstNameInput.input.value"
              v-model:dropdown-value="firstNameInput.dropdown!.value"
              v-model:label-value="firstNameInput.label"
            />
            <FormProfileGroupSection
              :form-element="lastNameInput"
              v-model:input-value="lastNameInput.input.value"
              v-model:dropdown-value="lastNameInput.dropdown!.value"
              v-model:label-value="lastNameInput.label"
            />
            <FormProfileGroupSection
              :form-element="primaryEmailInput"
              v-model:input-value="primaryEmailInput.input.value"
              v-model:dropdown-value="primaryEmailInput.dropdown!.value"
              v-model:label-value="primaryEmailInput.label"
            />

            <FormProfileGroupSection
              v-for="(item, index) in fixedProfilePropertyForm"
              :key="index"
              :form-element="item"
              v-model:input-value="item.input.value"
              v-model:dropdown-value="item.dropdown!.value"
              v-model:label-value="item.label"
            />
          </div>
        </div>
        <div class="row">
          <div class="col-12">
            <hr/>
          </div>
        </div>
        <div class="row">
          <div class="col-12 draggable-inputs">
            <draggable
              class="list-group"
              :component-data="{
                                tag: 'div',
                                type: 'transition-group',
                                name: !drag ? 'flip-list' : null,
                              }"
              v-model="customProfilePropertyForm"
              v-bind="dragOptions"
              @start="drag = true"
              @end="drag = false"
              item-key="id"
            >
              <template #item="{ element, index }">
                <FormProfileGroupSection
                  :key="element.id"
                  :p-key="index"
                  :form-element="element"
                  v-model:input-value="element.input.value"
                  v-model:label-value="element.label"
                  v-model:dropdown-value="element.dropdown.value"
                  :draggable="true"
                  @labelActionSelected="onLabelActionSelected"
                />
              </template>
            </draggable>
          </div>
        </div>
        <div class="row action">

          <div class="d-flex justify-content-end col-9">
            <ButtonElement
              @clickOnButton="saveForm()"
              class="btn--ms btn--accent"
              style="margin-left: 6px"
            >
              Save
            </ButtonElement>
          </div>
        </div>
      </div>
    </form>
  </ProfileTemplate>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import draggable from 'vuedraggable';
import {
  ButtonElement,
  FormProfileGroupSection,
  ProfileHeaderCardWidget,
  ProfileTemplate,
} from '@shared/components';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { v4 as uuidv4 } from 'uuid';
import profileProperties, { profileDropdownProperties } from './profile-properties';
import {AccountNs, CommonNs, type DropdownNs} from "@shared/types";
import {visibilityOptions} from "@shared/constants";
import {mapStores} from "pinia";
import {useAccountStore} from "@domains/account/stores/account.store";
import {useGeneralStore} from "@shared/stores/general.store";
import {toast} from '@shared/ms-components'

export default defineComponent({
  name: 'ProfileView',
  components: {
    ButtonElement,
    FormProfileGroupSection,
    ProfileHeaderCardWidget,
    ProfileTemplate,
    draggable,
  },
  data() {
    return {
      photoUploadStateActive: false,
      profileDropdownProperties: profileDropdownProperties as DropdownNs.DropdownOptionsItem[],
      profileProperties: profileProperties as CommonNs.InputFormGroup[],

      firstNameInput: {
        label: 'First Name',
        icon: 'User',
        iconType: 'fal',
        isFixed: true,
        input: {
          name: AccountNs.PropertyEnums.FIRST_NAME,
          type: 'text',
          placeholder: 'First Name',
          value: '',
        },
        dropdown: {
          name: 'firstNamePermission',
          options: visibilityOptions,
          value: '',
        },
      } as CommonNs.InputFormGroup,

      lastNameInput: {
        label: 'Last Name',
        icon: 'User',
        iconType: 'fal',
        isFixed: true,
        input: {
          name: AccountNs.PropertyEnums.LAST_NAME,
          type: 'text',
          placeholder: 'Last Name',
          value: '',
        },
        dropdown: {
          name: 'lastNamePermission',
          options: visibilityOptions,
          value: '',
        },
      } as CommonNs.InputFormGroup,

      primaryEmailInput: {
        label: 'Primary Email',
        icon: 'Mail',
        iconType: 'fal',
        isFixed: true,
        input: {
          name: AccountNs.PropertyEnums.PRIMARY_EMAIL,
          type: 'text',
          placeholder: 'md@flexwork.io',
          value: {},
          isDisable: true,
          isLocked: true,
          callbackButton: {
            type: '',
            label: 'Change Email',
            class: 'btn-grey',
            callback: this.redirectToChangePrimaryMail,
          },
        },
        dropdown: {
          name: 'primaryEmailPermission',
          options: visibilityOptions,
          value: '',
        },
      } as CommonNs.InputFormGroup,

      fixedProfilePropertyForm: [] as CommonNs.InputFormGroup[],
      customProfilePropertyForm: [] as CommonNs.InputFormGroup[],

      drag: false,
      message: ['vue.draggable'],
      order: 1,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      form: {
        firstName: { required },
        lastName: { required },
        primaryEmail: { required },
      },
    };
  },
  computed: {
    ...mapStores(useAccountStore, useGeneralStore),
    dragOptions() {
      return {
        animation: 200,
        group: 'descriptions',
        disabled: false,
        ghostClass: 'ghosty',
      };
    },
  },
  mounted () {
    // // TODO Bunları unsubscribe yap
    // websocket.wsConnectSubject.subscribe(() => {
    //   websocket.subscribe('/user/queue/notification', (message: any) => {
    //     console.log(message)
    //   })
    // })
    // websocket.subscribe('/user/queue/notification', (message: any) => {
    //   console.log(message)
    // })
  },
  beforeMount() {
    this.initFormData();
  },
  methods: {
    initFormData() {
      this.initUserInfo();
      this.initFixedProperties();
      this.initAccountProperties();
    },

    initUserInfo() {
      const account = this.accountStore.getAccount;

      const firstNameProperty = account.accountProperties?.find((a: AccountNs.AccountProperty) => a.name === AccountNs.PropertyEnums.FIRST_NAME);
      this.firstNameInput.input.value = account.firstName;
      this.firstNameInput.dropdown!.value = firstNameProperty?.permission ?? CommonNs.PermissionsEnums.EVERYONE;

      const lastNameProperty = account.accountProperties?.find((a: AccountNs.AccountProperty) => a.name === AccountNs.PropertyEnums.LAST_NAME);
      this.lastNameInput.input.value = account.lastName;
      this.lastNameInput.dropdown!.value = lastNameProperty?.permission ?? CommonNs.PermissionsEnums.EVERYONE;

      const primaryEmailProperty = account.accountProperties?.find((a: AccountNs.AccountProperty) => a.name === AccountNs.PropertyEnums.PRIMARY_EMAIL);
      this.primaryEmailInput.input.value = account.userName;
      this.primaryEmailInput.dropdown!.value = primaryEmailProperty?.permission ?? CommonNs.PermissionsEnums.EVERYONE;
    },

    /** Iterate all profileProperties and filter if it is a fixed property Add these… */
    initFixedProperties() {
      const account = this.accountStore.getAccount;

      this.fixedProfilePropertyForm = [] as CommonNs.InputFormGroup[];
      // Find Fixed properties
      const fixedProperties = this.profileProperties.filter((p: CommonNs.InputFormGroup) => p.isFixed);
      // Add these properties to fixedProfilePropertyForm
      fixedProperties.forEach((prop: CommonNs.InputFormGroup) => {
        const p = JSON.parse(JSON.stringify(prop));
        // Find the value of this property if present
        const propertyInAccount = account.accountProperties?.find((a: AccountNs.AccountProperty) => a.name === p.input.name);
        if (propertyInAccount) {
          p.input.value = propertyInAccount.inputValue;
          p.dropdown!.value = propertyInAccount.permission ?? CommonNs.PermissionsEnums.EVERYONE;
        }
        this.fixedProfilePropertyForm.push(p);

        this.deleteFromDropdownMenu(p.input.name);
      });
    },

    /** Iterate all account properties and find corresponding item in… */
    initAccountProperties() {
      const account = this.accountStore.getAccount;

      this.customProfilePropertyForm = [] as CommonNs.InputFormGroup[];
      account.accountProperties?.forEach((a: AccountNs.AccountProperty,
        index: number) => {
        let searchedComponentName = a.name;
        if (a.isCustom) {
          searchedComponentName = AccountNs.PropertyEnums.CUSTOM;
        }

        const prop = this.profileProperties.find((pp: CommonNs.InputFormGroup) => searchedComponentName === pp.input.name);
        if (prop && !prop.isFixed) {
          const p = JSON.parse(JSON.stringify(prop));
          p.input.value = a.inputValue;
          p.dropdown!.value = a.permission ?? CommonNs.PermissionsEnums.EVERYONE;
          p.id = index;

          // Find the value of this property if present
          if (a.isCustom) {
            p.input.name = a.name;
            p.input.type = a.type;
            p.icon = a.icon;
            p.iconType = a.iconType;
            p.label = a.label;
            p.isCustom = true;
          } else {
            this.deleteFromDropdownMenu(p.input.name);
          }
          this.customProfilePropertyForm.push(p);
        }
      });
    },

    /** Delete this from Dropdown Menu, so the user can not add it twice */
    deleteFromDropdownMenu(subMenuName: string) {
      this.profileDropdownProperties.forEach((item: DropdownNs.DropdownOptionsItem) => {
        const index = item.subOptions?.options.findIndex((subItem: DropdownNs.DropdownOptionsItem) => subItem.value === subMenuName);
        if (index != undefined && (index > -1)) {
          item.subOptions?.options.splice(index, 1);
        }
      });
    },

    saveForm() {
      const customProperties = this.customProfilePropertyForm
        .map((item: CommonNs.InputFormGroup) => ({
          name: item.input.name,
          label: item.label,
          type: item.input.type,
          isCustom: item.isCustom,
          inputValue: item.input.value,
          permission: item.dropdown?.value,
          icon: item.icon,
          iconType: item.iconType,
        } as AccountNs.AccountProperty));

      const fixedProperties = this.fixedProfilePropertyForm
        .map((item: CommonNs.InputFormGroup) => ({
          name: item.input.name,
          label: item.label,
          type: item.input.type,
          isCustom: item.isCustom,
          inputValue: item.input.value,
          permission: item.dropdown?.value,
        } as AccountNs.AccountProperty));

      fixedProperties.push({
        name: this.firstNameInput.input.name,
        label: this.firstNameInput.label,
        type: this.firstNameInput.input.type,
        isCustom: this.firstNameInput.isCustom,
        inputValue: this.firstNameInput.input.value,
        permission: this.firstNameInput.dropdown?.value,
      } as AccountNs.AccountProperty);

      fixedProperties.push({
        name: this.lastNameInput.input.name,
        label: this.lastNameInput.label,
        type: this.lastNameInput.input.type,
        isCustom: this.lastNameInput.isCustom,
        inputValue: this.lastNameInput.input.value,
        permission: this.lastNameInput.dropdown?.value,
      } as AccountNs.AccountProperty);

      fixedProperties.push({
        name: this.primaryEmailInput.input.name,
        label: this.primaryEmailInput.label,
        type: this.primaryEmailInput.input.type,
        isCustom: this.primaryEmailInput.isCustom,
        inputValue: this.primaryEmailInput.input.value,
        permission: this.primaryEmailInput.dropdown?.value,
      } as AccountNs.AccountProperty);

      const payload: AccountNs.UpdateAccountPropertyRequest = {
        firstName: this.firstNameInput.input.value,
        lastName: this.lastNameInput.input.value,
        accountProperties: [...fixedProperties, ...customProperties],
      };

      this.accountStore.updateProperties(payload).then(() => {
        toast.success('Saved');
        this.initFormData();
      }).catch(() => {
        toast.success('Something went wrong. Please try again.');
      })
    },

    appendPropertyToForm(input: DropdownNs.DropdownOptionsItem) {
      const prop = this.profileProperties.find((pp: CommonNs.InputFormGroup) => input.value === pp.input.name);
      if (prop) {
        const p = JSON.parse(JSON.stringify(prop));
        const maxId: number = Math.max.apply(Math, this.customProfilePropertyForm.map((item) => item.id!));
        p.id = (maxId > 0 ? maxId : 0) + 1;
        p.isCustom = (input.value === AccountNs.PropertyEnums.CUSTOM);

        if (p.isCustom) {
          p.input.name = uuidv4();
          p.input.type = input.inputType;
          p.icon = input.icon;
          p.iconType = input.iconType;
        } else {
          this.deleteFromDropdownMenu(p.input.name);
        }

        this.customProfilePropertyForm.push(p);
      }
    },

    onLabelActionSelected(selectedAction: string, selectedItemIndex: number) {
      if (selectedAction === 'DUPLICATE') {
        const prop = this.customProfilePropertyForm[selectedItemIndex];
        if (prop && prop.isCustom) {
          const p = JSON.parse(JSON.stringify(prop));
          const maxId: number = Math.max.apply(Math, this.customProfilePropertyForm.map((item) => item.id!));
          p.id = (maxId > 0 ? maxId : 0) + 1;
          this.customProfilePropertyForm.splice(selectedItemIndex + 1, 0, p);
        }
      } else if (selectedAction === 'DELETE') {
        this.customProfilePropertyForm.splice(selectedItemIndex, 1);
      }
    },

    redirectToChangePrimaryMail() {
      this.$router.push({ name: 'AccountEmail' });
    },
  },
});
</script>

<style lang="scss" scoped>
.action {
  margin-top: 20px;
}
</style>
